// src/components/ChatbotWidget.jsx
import { useEffect, useMemo, useRef, useState } from "react";

export default function ChatbotWidget({
  botId = "68ad8b4447f38e1741004a04",
  desktop = { width: "420px", height: "600px", right: "30px" },
  mobile = { width: "90%", height: "calc(100vh - 100px)", right: "5%" },
  corner = "bottom-right", // "bottom-right" | "bottom-left"
  zIndex = 1000,
  borderRadius = "10px",
}) {
  const iframeRef = useRef(null);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  // Recalcule sur resize (optionnel mais plus robuste)
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const dims = useMemo(
    () => (isMobile ? mobile : desktop),
    [isMobile, mobile, desktop]
  );

  // Applique les styles “ouverts”
  const applyOpenSize = () => {
    const el = iframeRef.current;
    if (!el) return;
    el.style.width = dims.width;
    el.style.height = dims.height;
  };

  // Applique les styles “réduits”
  const applyClosedSize = () => {
    const el = iframeRef.current;
    if (!el) return;
    el.style.width = "50px";
    el.style.height = "70px";
  };

  useEffect(() => {
    // Créer l’iframe une seule fois
    const el = document.createElement("iframe");
    el.src = `https://www.app.limova.ai/embededChatbot?id=${botId}`;
    el.title = "Limova Chatbot";
    el.setAttribute("aria-label", "Chatbot");
    el.referrerPolicy = "no-referrer-when-downgrade";

    // Position
    const baseStyles = {
      position: "fixed",
      bottom: "0",
      border: "none",
      zIndex: String(zIndex),
      borderRadius,
      // coin
      left:
        corner === "bottom-left"
          ? isMobile
            ? mobile.right
            : desktop.right
          : "",
      right:
        corner === "bottom-right"
          ? isMobile
            ? mobile.right
            : desktop.right
          : "",
    };

    Object.assign(el.style, baseStyles);
    document.body.appendChild(el);
    iframeRef.current = el;

    // Taille initiale (ouverte)
    applyOpenSize();

    // Ecoute les messages pour open/close
    const onMessage = (evt) => {
      const d = evt?.data;
      if (!d || typeof d !== "object") return;
      if (d.chatbotOpen === true) applyOpenSize();
      if (d.chatbotOpen === false) applyClosedSize();
    };

    window.addEventListener("message", onMessage);

    // Cleanup
    return () => {
      window.removeEventListener("message", onMessage);
      if (iframeRef.current?.parentNode) {
        iframeRef.current.parentNode.removeChild(iframeRef.current);
      }
      iframeRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    botId,
    corner,
    zIndex,
    borderRadius,
    desktop.right,
    mobile.right,
    isMobile,
  ]);

  // Quand le viewport change, réappliquer la taille ouverte courante
  useEffect(() => {
    applyOpenSize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dims.width, dims.height]);

  // Optionnel : méthodes pour contrôler depuis ta UI (exemple)
  const open = () => {
    // on simule le message attendu par le script embarqué
    applyOpenSize();
  };
  const close = () => {
    applyClosedSize();
  };

  // Rien à rendre : le widget est un “portal” ajouté au body
  return null;
}
